import React, { useCallback, useEffect, useState } from 'react';
import { NSButton, NSDropdown } from 'bricks';
import './BackOfEnvelope.scss';
import { faCalculator, faPlus, faScaleBalanced } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { formatDate } from 'ns_libs/formatter';
import { IOptionAnyValue } from 'bricks/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import { useSlideout } from 'contexts/BackOfEnvelopeContext';
import { SimplifiedScenario } from 'components/DealScenarioDropdown/types';
import { Row } from 'reactstrap';
import useCreateProForma from 'queries/ProForma/useCreateProForma';
import CreateProFormaModal from 'views/CreateProFormaModal/CreateProFormaModal';
import ScenarioInformation from './components/ScenarioInformation';
import ScenarioReturns from './components/ScenarioReturns';
import { useGetBackOfEnvelope } from './hooks/useGetBOE';

type IProps = {
    orgId: string;
    boeScenarios?: SimplifiedScenario[];
    dealId: number;
    handleScenarioChange: (scenarioId: number) => void;
    defaultScenarioId?: number | undefined;
};

const BackOfEnvelope = ({
    orgId, dealId, boeScenarios, handleScenarioChange, defaultScenarioId,
}: IProps) => {
    const navigate = useNavigate();
    const createProFormaMutation = useCreateProForma();

    const {
        toggleSlideout,
        selectedScenarioId,
        selectedScenario,
        setSelectedScenarioId,
        setSelectedScenario,
        setSelectedSlideoutScenarioId,
        getSlideoutValues,
    } = useSlideout();

    const { data: fetchedDeal } = useGetDealById({
        orgId,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const { data: overviewScenarioData } = useGetBackOfEnvelope({
        orgId,
        scenarioId: selectedScenarioId,
    });

    const [openCreateProFormaModal, setOpenCreateProFormaModal] = useState(false);
    const toggleOpenProformaModal = () => setOpenCreateProFormaModal(!openCreateProFormaModal);

    const simplifiedOverviewScenarios = boeScenarios?.map(item => ({ value: item.id, label: item.name }));

    const handleViewComparison = useCallback(() => {
        navigate(`/deals/comparison/${fetchedDeal?.id}`, {
            state: {
                orgId,
                deal: fetchedDeal,
                scenarios: boeScenarios,
                selectedScenario: overviewScenarioData,
            },
        });
    }, [navigate, fetchedDeal, simplifiedOverviewScenarios, overviewScenarioData]);

    const handleSelectScenario = useCallback((option: IOptionAnyValue) => {
        const scenarioId = option.id || option.value;

        setSelectedScenario(option);
        setSelectedScenarioId(scenarioId);
        setSelectedSlideoutScenarioId(undefined);
        handleScenarioChange(scenarioId);
    }, []);

    useEffect(() => {
        // If we have scenarios but the currently selected one doesn't exist anymore
        // or if we have no selected scenario but have available scenarios
        if (simplifiedOverviewScenarios?.length) {
            const scenarioExists = selectedScenarioId
                && simplifiedOverviewScenarios.some(scenario => scenario.value === selectedScenarioId);

            if (!scenarioExists) {
                // If we have a default scenario and it exists in our scenarios
                if (defaultScenarioId
                    && simplifiedOverviewScenarios.some(scenario => scenario.value === defaultScenarioId)) {
                    const defaultOption = simplifiedOverviewScenarios.find(
                        scenario => scenario.value === defaultScenarioId,
                    );
                    setSelectedScenario(defaultOption);
                    setSelectedScenarioId(defaultScenarioId);
                    handleScenarioChange(defaultScenarioId);
                } else {
                    // Otherwise select the first available scenario
                    const firstScenario = simplifiedOverviewScenarios[0];
                    setSelectedScenario({
                        label: firstScenario?.label || '',
                        value: Number(firstScenario?.value),
                    });
                    setSelectedScenarioId(Number(firstScenario?.value));
                    handleScenarioChange(Number(firstScenario?.value));
                }
            }
        }
    }, [boeScenarios,
        simplifiedOverviewScenarios,
        defaultScenarioId,
        selectedScenarioId,
        setSelectedScenario,
        setSelectedScenarioId,
        handleScenarioChange]);

    const handleToggleSlideout = () => {
        if (overviewScenarioData && simplifiedOverviewScenarios && simplifiedOverviewScenarios.length > 0) {
            getSlideoutValues();
        }
        toggleSlideout();
    };

    const EmptyState = (
        <div className="d-flex flex-column align-items-center justify-content-center text-center h-100">
            <h4 className="text-white mb-1 font-20">No scenarios yet.</h4>
            <p className="text-muted d-flex align-items-center justify-content-center">
                Create your first return scenario using the
                {' '}
                <NSButton className="text-primary p-0 mx-1" callback={handleToggleSlideout} allowReadOnlyAccess>
                    <FontAwesomeIcon icon={faCalculator} className="text-primary px-1" aria-label="calculator" />
                    Back-Of-Envelope
                </NSButton>
                {' '}
                tool.
            </p>
        </div>
    );

    return (
        <div className="BackOfEnvelope d-flex flex-column pb-2">
            <div className="d-flex justify-content-between align-items-center w-100 py-2 px-3">
                <div className="text-left">
                    <h3 className="text-white mb-0">Back-Of-Envelope</h3>
                    <p className="text-muted mb-0">Assess the financial feasibility of this deal.</p>
                </div>
                <div className="d-flex align-items-center">
                    <RenderIf isTrue={simplifiedOverviewScenarios && simplifiedOverviewScenarios.length > 1}>
                        {simplifiedOverviewScenarios && (
                            <NSDropdown
                                placeholder="Select existing scenario"
                                options={simplifiedOverviewScenarios}
                                selectedOption={simplifiedOverviewScenarios.find(
                                    option => option.value === (defaultScenarioId || selectedScenario?.value),
                                )}
                                handleSelectOption={handleSelectScenario}
                            />
                        )}
                    </RenderIf>
                    <NSButton
                        text="Compare"
                        color="secondary"
                        icon={faScaleBalanced}
                        className="mx-2"
                        callback={handleViewComparison}
                    />
                    <NSButton
                        data-testid="back-of-envelope-button"
                        text="Back-Of-Envelope"
                        color="secondary"
                        icon={faCalculator}
                        callback={handleToggleSlideout}
                        allowReadOnlyAccess
                    />
                </div>
            </div>

            {overviewScenarioData && simplifiedOverviewScenarios && simplifiedOverviewScenarios.length > 0 ? (
                <div className="BackOfEnvelope__ScenarioContainer NSCard--level-2 py-2 px-3 mx-3 my-1">
                    <div className="BackOfEnvelope__ScenarioContainer--header d-flex flex-row justify-content-between align-items-center pb-2 mb-2">
                        <div>
                            <h4 className="text-white font-weight-bold mb-0">{overviewScenarioData.name}</h4>
                            <h5 className="mt-1">{`Created on ${formatDate(overviewScenarioData.createdAt)}`}</h5>
                        </div>
                        <Row>
                            <Row className="BackOfEnvelope__ScenarioContainer--border d-flex align-items-center rounded">
                                <div className="text-muted border px-2 py-1 rounded">
                                    Asset class:
                                    &nbsp;
                                    <span className="text-dark text-capitalize">{overviewScenarioData?.propertyType?.name}</span>
                                </div>
                            </Row>
                            <div className="border-left pl-2 ml-3" />
                            <NSButton
                                callback={toggleOpenProformaModal}
                                text="Generate pro forma"
                                color="primary"
                                icon={faPlus}
                                isLoading={createProFormaMutation.isPending}
                            />
                        </Row>
                    </div>
                    <ScenarioReturns boeScenario={overviewScenarioData} deal={fetchedDeal || undefined} />
                    <ScenarioInformation boeScenario={overviewScenarioData} />
                </div>
            ) : (
                EmptyState
            )}

            <RenderIf isTrue={openCreateProFormaModal}>
                <CreateProFormaModal toggle={toggleOpenProformaModal} isOpen={openCreateProFormaModal} initialScenario={selectedScenario} />
            </RenderIf>
        </div>
    );
};

export default BackOfEnvelope;
